import { ThisConnector } from '../../../../api/connector'
import { SelectedValueReportingFacet } from '../../../../api/queries/value-reporting/vr-vulnerabilities'
import { mapApiGradeToUiGrade } from '../../../../graphql/schema/queries/value-reporting/map-api-grade-to-ui-grade'
import { mapNegativeNumberToZero } from '../../../../graphql/schema/queries/value-reporting/map-negative-number-to-zero'
import { GradeValue } from '../../../../graphql/schema/types/grade-value'

export async function ratingsRequest(
  this: ThisConnector,
  {
    organizationUuid,
    selectedFacets
  }: {
    organizationUuid: string
    selectedFacets: SelectedValueReportingFacet[]
  }
) {
  const { facets: apiFacets } = await this.connector.post(
    `/organizations/${organizationUuid}/application-security-summary/ratings`,
    { facets: selectedFacets }
  )

  const mappedFacets = normalizeFacets(apiFacets)

  return {
    totalApplications:
      apiFacets.length > 0
        ? mapNegativeNumberToZero(apiFacets[0].totalCount)
        : 0,
    facets: {
      customCode: {
        grade: mappedFacets.customCodeRating.grade,
        total: mappedFacets.customCodeRating.totalCount,
        values: mapValues(mappedFacets.customCodeRating.values)
      },
      libraries: {
        grade: mappedFacets.libraryRating.grade,
        total: mappedFacets.libraryRating.totalCount,
        values: mapValues(mappedFacets.libraryRating.values)
      },
      overall: mapOverallRatingsData(mappedFacets.overallRating)
    }
  }
}

const requiredGrades: GradeValue[] = ['A', 'B', 'C', 'D', 'F']

const mapValues = (values: VrRatingsFacetValue[]) => {
  // Map the data so that the required grades are always present
  return requiredGrades.map((grade) => {
    const found = values.find((v) => v.value === grade)
    return {
      value: grade,
      count: found ? found.count : 0
    }
  })
}

export const mapOverallRatingsData = (data: VrRatingsFacet) => {
  const { values, score, grade, totalCount } = data
  return {
    overallGrade: grade,
    overallScore: score,
    appCount: totalCount,
    viewData: mapValues(values)
  }
}

type ApiFacetName = 'custom-code-rating' | 'library-rating' | 'overall-rating'
type UiFacetName = 'customCodeRating' | 'libraryRating' | 'overallRating'

const apiToUiFacetNameMap: Record<ApiFacetName, UiFacetName> = {
  'custom-code-rating': 'customCodeRating',
  'library-rating': 'libraryRating',
  'overall-rating': 'overallRating'
}

export interface VrRatingsFacetValue {
  count: number
  displayName: string
  value: string
}

export interface VrRatingsFacet {
  grade: GradeValue
  score: number
  totalCount: number
  values: VrRatingsFacetValue[]
}

export interface VrRatingsResolverResponse {
  totalApplications: number
  facets: Record<UiFacetName, VrRatingsFacet>
}

export interface VrApiFacet {
  name: ApiFacetName
  displayName: string
  totalCount: number
  values: ApiFacetValue[]
  grade: string
  score: number
}

interface ApiFacetValue {
  value: string
  displayName: string
  count: number
}

const initialFacet: VrRatingsFacet = {
  grade: 'UNKNOWN',
  score: -1,
  totalCount: -1,
  values: []
}

const normalizeFacetValues = (values: ApiFacetValue[]) =>
  values.map(({ value, displayName, count }) => ({
    value: mapApiGradeToUiGrade(value),
    displayName,
    count: mapNegativeNumberToZero(count)
  }))

const normalizeFacet = ({
  name,
  displayName,
  totalCount,
  grade,
  score,
  values
}: {
  name: UiFacetName
} & Pick<
  VrApiFacet,
  'displayName' | 'totalCount' | 'grade' | 'score' | 'values'
>) => ({
  name,
  displayName,
  totalCount: mapNegativeNumberToZero(totalCount),
  grade: mapApiGradeToUiGrade(grade),
  score: mapNegativeNumberToZero(score),
  values: normalizeFacetValues(values)
})

const normalizeFacets = (apiFacets: VrApiFacet[]) => {
  return apiFacets.reduce<Record<UiFacetName, VrRatingsFacet>>(
    (prev, { name, ...apiFacet }) => {
      if (apiToUiFacetNameMap.hasOwnProperty(name)) {
        const uiFacetName = apiToUiFacetNameMap[name]
        return {
          ...prev,
          [uiFacetName]: normalizeFacet({
            name: uiFacetName,
            ...apiFacet
          })
        }
      }
      return prev
    },
    {
      customCodeRating: initialFacet,
      libraryRating: initialFacet,
      overallRating: initialFacet
    }
  )
}
